import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { useAuth } from './AuthContext';

type LanguageType = 'en' | 'ru' | 'ja' | 'zh' | 'de' | 'fr';

interface LanguageContextType {
  language: LanguageType;
  changeLanguage: (lng: LanguageType) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<LanguageType>((i18n.language as LanguageType) || 'en');
  const [isLoading, setIsLoading] = useState(false);

  // При авторизации загружаем предпочтительный язык пользователя из Firestore
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists() && userDoc.data().language) {
            const userLanguage = userDoc.data().language as LanguageType;
            // Устанавливаем язык только если он отличается от текущего
            if (userLanguage !== i18n.language) {
              await i18n.changeLanguage(userLanguage);
              setLanguage(userLanguage);
            }
          } else {
            // Если у пользователя нет предпочтительного языка,
            // сохраняем текущий язык в его профиль
            await updateDoc(userRef, {
              language: i18n.language
            });
          }
        } catch (error) {
          console.error('Error loading user language:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadUserLanguage();
  }, [user, i18n]);

  const changeLanguage = async (lng: LanguageType) => {
    setIsLoading(true);
    try {
      // Изменяем язык в i18n
      await i18n.changeLanguage(lng);
      
      // Сохраняем выбранный язык в localStorage
      localStorage.setItem('language', lng);
      
      // Обновляем состояние
      setLanguage(lng);
      
      // Если пользователь авторизован, сохраняем выбор в Firestore
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          language: lng
        });
      }
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}; 